import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Skeleton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import PhoneIcon from "@mui/icons-material/Phone";
import MailIcon from "@mui/icons-material/Mail";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import AddIcon from "@mui/icons-material/Add";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { customGetAPI } from "services/utils/CustomAPI";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  setFrajunPhoneNumber,
  setFrajunContactName,
  setFrajunAutoInitiate,
  setFrajunContactId,
  setVisible,
} from "services/Redux/userToken";
import ContactTimeline from "components/contacts/ContactTimeline";
import ReminderForm from "components/contacts/ReminderForm";
import LogActivityDialog from "components/contacts/LogActivityDialog";
import axios from "axios";
import { format } from "date-fns";

const isValidE164 = (num) =>
  /^\+[1-9]\d{6,14}$/.test((num || "").replace(/\s/g, ""));

function ContactRemindersPanel({ contact }) {
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${domain}crm/reminders/`, {
        params: { contact_id: contact.id, limit: 50, sort: "-date" },
        headers: { Authorization: `token ${token}` },
      });
      setReminders(res.data.results || []);
    } catch {}
    setLoading(false);
  }, [domain, token, contact.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2" fontWeight={600}>Reminders</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => { setEditingReminder(null); setShowForm(true); }}
          sx={{ background: "#1D9E75", "&:hover": { background: "#0F6E56" } }}
        >
          Add Reminder
        </Button>
      </Box>

      {reminders.length === 0 ? (
        <Box textAlign="center" py={4} color="text.secondary">
          <NotificationsIcon sx={{ fontSize: 32, mb: 1, color: "text.disabled" }} />
          <Typography>No reminders set</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {reminders.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography fontWeight={500} variant="body2">{r.title}</Typography>
              {r.notes && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  {r.notes}
                </Typography>
              )}
              <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                <Chip label={r.status} size="small" />
                {r.date && (
                  <Chip
                    label={format(new Date(r.date), "MMM d, yyyy h:mm a")}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {showForm && (
        <ReminderForm
          contactId={contact.id}
          reminder={editingReminder}
          onSave={() => { setShowForm(false); setEditingReminder(null); load(); }}
          onCancel={() => { setShowForm(false); setEditingReminder(null); }}
        />
      )}
    </Box>
  );
}

export default function ContactProfilePage() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { domain } = useSelector((state) => state.tokenReducer);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState("timeline");
  const [logActivityOpen, setLogActivityOpen] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [activeCallInProgress, setActiveCallInProgress] = useState(
    Boolean(localStorage.getItem("active_call_uuid"))
  );

  useEffect(() => {
    const refresh = () =>
      setActiveCallInProgress(Boolean(localStorage.getItem("active_call_uuid")));
    refresh();
    window.addEventListener("storage", refresh);
    const interval = setInterval(refresh, 2500);
    return () => {
      window.removeEventListener("storage", refresh);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    customGetAPI(`${domain}crm/contacts/${id}/`)
      .then((res) => {
        const data = res?.data ?? res?.responseDetails;
        if (data) {
          setContact(data);
        } else {
          setError(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [id, domain]);

  const primaryPhone =
    contact?.phone_numbers?.find((p) => p.primary)?.number ||
    contact?.phone_numbers?.[0]?.number ||
    null;

  const canCall =
    Boolean(primaryPhone && isValidE164(primaryPhone)) && !activeCallInProgress;

  const handleClickToCall = (phoneNumber) => {
    if (activeCallInProgress) {
      toast.warning(
        "A call is already active. Please end the current call before starting a new one."
      );
      return;
    }
    if (!isValidE164(phoneNumber)) return;
    const contactName =
      contact.company || `${contact.first_name} ${contact.last_name}`;
    dispatch(setFrajunContactId(contact.id));
    dispatch(setFrajunPhoneNumber(phoneNumber));
    dispatch(setFrajunContactName(contactName));
    dispatch(setFrajunAutoInitiate(false));
    dispatch(setVisible(true));
    toast.success(`Initiating call to ${contactName}`);
  };

  const getInitials = (c) => {
    if (!c) return "";
    if (c.company) return c.company[0]?.toUpperCase() || "";
    return `${c.first_name?.[0]?.toUpperCase() || ""}${c.last_name?.[0]?.toUpperCase() || ""}`;
  };

  if (loading) {
    return (
      <Box p={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Skeleton variant="circular" width={72} height={72} />
              <Box flex={1}>
                <Skeleton variant="text" width="40%" height={36} />
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="50%" />
              </Box>
            </Box>
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 2 }} />
            <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !contact) {
    return (
      <Box
        p={4}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={300}
      >
        <Typography variant="h6" color="text.secondary" mb={2}>
          Contact not found
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push("/admin/crm/contacts")}
        >
          Back to Contacts
        </Button>
      </Box>
    );
  }

  const primaryAddr = contact.addresses?.[0];
  const locationStr = [primaryAddr?.city, primaryAddr?.country]
    .filter(Boolean)
    .join(", ");

  return (
    <Box p={4}>
      <Grid container spacing={3}>
        {/* LEFT COLUMN */}
        <Grid item xs={12} md={7}>
          {/* Page header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/admin/crm/contacts")}
              sx={{ color: "text.secondary" }}
            >
              Contacts
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => history.push(`/admin/crm/edit/${contact.id}`)}
              >
                Edit
              </Button>
              {primaryPhone && (
                <Button
                  variant="contained"
                  startIcon={<PhoneIcon />}
                  disabled={!canCall}
                  sx={{ background: "#1D9E75", "&:hover": { background: "#0F6E56" } }}
                  onClick={() => handleClickToCall(primaryPhone)}
                >
                  Call
                </Button>
              )}
            </Box>
          </Box>

          {/* Hero card */}
          <Paper
            variant="outlined"
            sx={{ p: 3, mb: 2, borderRadius: 2, textAlign: "center" }}
          >
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#1D9E75",
                fontSize: "1.5rem",
                mx: "auto",
                mb: 2,
              }}
            >
              {getInitials(contact)}
            </Avatar>
            <Typography variant="h5" fontWeight={700}>
              {contact.first_name} {contact.last_name}
            </Typography>
            {(contact.company || contact.position) && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {[contact.company, contact.position].filter(Boolean).join(" · ")}
              </Typography>
            )}
            <Box display="flex" justifyContent="center" gap={1} mt={1.5} flexWrap="wrap">
              {contact.status_name && (
                <Chip
                  label={contact.status_name}
                  size="small"
                  sx={{ backgroundColor: contact.status_color, color: "#fff" }}
                />
              )}
              {contact.source_name && (
                <Chip label={contact.source_name} size="small" variant="outlined" />
              )}
            </Box>
          </Paper>

          {/* Contact Information */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Contact Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {contact.emails?.map((e, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1.5}>
                  <MailIcon
                    sx={{ color: "text.secondary", fontSize: 18, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    component="a"
                    href={`mailto:${e.email}`}
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {e.email}
                  </Typography>
                </Box>
              ))}
              {contact.phone_numbers?.map((p, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1.5}>
                  <PhoneIcon
                    sx={{ color: "text.secondary", fontSize: 18, flexShrink: 0 }}
                  />
                  <Typography variant="body2" flex={1}>
                    {p.number}
                  </Typography>
                  <Tooltip
                    title={
                      activeCallInProgress
                        ? "Active call in progress"
                        : !isValidE164(p.number)
                        ? "Requires E.164 format (e.g. +91 9876543210)"
                        : `Call ${p.number}`
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={activeCallInProgress || !isValidE164(p.number)}
                        onClick={() => handleClickToCall(p.number)}
                        sx={{
                          color: "#1D9E75",
                          padding: "4px",
                          "&:hover": { background: "#E1F5EE" },
                          "&.Mui-disabled": { color: "action.disabled" },
                        }}
                      >
                        <PhoneIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
              {locationStr && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <LocationOnIcon
                    sx={{ color: "text.secondary", fontSize: 18, flexShrink: 0 }}
                  />
                  <Typography variant="body2">{locationStr}</Typography>
                </Box>
              )}
              {contact.company && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <BusinessIcon
                    sx={{ color: "text.secondary", fontSize: 18, flexShrink: 0 }}
                  />
                  <Typography variant="body2">{contact.company}</Typography>
                </Box>
              )}
              {contact.position && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <WorkIcon
                    sx={{ color: "text.secondary", fontSize: 18, flexShrink: 0 }}
                  />
                  <Typography variant="body2">{contact.position}</Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* CRM Intelligence */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              CRM Intelligence
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
              {contact.source_name && (
                <Chip label={contact.source_name} size="small" variant="outlined" />
              )}
              {contact.status_name && (
                <Chip
                  label={contact.status_name}
                  size="small"
                  sx={{ backgroundColor: contact.status_color, color: "#fff" }}
                />
              )}
              {contact.username && (
                <Chip label={contact.username} size="small" variant="outlined" />
              )}
            </Box>
            {contact.score != null && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Score: {contact.score}
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    height: 6,
                    background: "#EEE",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${Math.min(contact.score ?? 0, 100)}%`,
                      background: "#1D9E75",
                      borderRadius: 3,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} md={5}>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLogActivityOpen(true)}
              sx={{ background: "#1D9E75", "&:hover": { background: "#0F6E56" } }}
            >
              Add Activity
            </Button>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Timeline" value="timeline" />
            <Tab label="Calls" value="calls" />
            <Tab label="Reminders" value="reminders" />
          </Tabs>

          {activeTab === "timeline" && (
            <ContactTimeline
              contactId={contact.id}
              refreshKey={timelineRefreshKey}
            />
          )}
          {activeTab === "calls" && (
            <ContactTimeline
              contactId={contact.id}
              refreshKey={timelineRefreshKey}
            />
          )}
          {activeTab === "reminders" && (
            <ContactRemindersPanel contact={contact} />
          )}
        </Grid>
      </Grid>

      <LogActivityDialog
        open={logActivityOpen}
        onClose={() => setLogActivityOpen(false)}
        contact={contact}
        preselectedType={null}
        onSaved={() => {
          setTimelineRefreshKey((k) => k + 1);
        }}
      />
    </Box>
  );
}
